import { FetchDefaultOptions, GeeksApi } from '@/api';
import { SELECT_SEARCH_CHAR_THRESHOLD } from '@/main';
import { AdminGroup, Empty, Geek, GeekItem, GeekItemWithJobTitle, GeekJobInfo, GeekNames } from '@/models';
import { buildGeekFullName, groupBy, nameof } from '@/utils';
import { Module } from 'vuex';
import { Actions } from './actions';
import { Getters } from './getters';
import { Mutations } from './mutations';
import { RootState } from './root.store';

const geeksApi = new GeeksApi();

export interface LabeledGeek {
  label: string;
}

export interface GeekState {
  current: Geek;
  geek: GeekItem;
  allGeeks: GeekItem[];
  admins: GeekItemWithJobTitle[];
  superAdmins: GeekItemWithJobTitle[];
  geekJobInfo: GeekJobInfo;
  geekSharedFilter: GeekItem;
}

export const GeekModule: Module<GeekState, RootState> = {
  state: {
    current: Empty.geek,
    geek: Empty.geekItem as GeekItem,
    allGeeks: [],
    admins: [],
    superAdmins: [],
    geekJobInfo: Empty.geekJobInfo as GeekJobInfo,
    geekSharedFilter: null as unknown as GeekItem,
  },

  mutations: {
    [Mutations.geek.setCurrent](state, geek: Geek) {
      state.current = geek;
    },
    [Mutations.geek.setCsrfToken](_, token: string) {
      FetchDefaultOptions.csrfToken = token;
    },
    [Mutations.geek.setGeeks](state, geeks: Geek[]) {
      state.allGeeks = geeks;
    },
    [Mutations.geek.setAdmins](state, admins: GeekItemWithJobTitle[]) {
      state.admins = admins;
    },
    [Mutations.geek.setSuperAdmins](state, superAdmins: GeekItemWithJobTitle[]) {
      state.superAdmins = superAdmins;
    },
    [Mutations.geek.setGeekBasicInfo](state, basicInfo: GeekItem) {
      state.geek = basicInfo;
    },
    [Mutations.geek.setGeekJobInfo](state, geekJobInfo: GeekJobInfo) {
      state.geekJobInfo = geekJobInfo;
    },
    [Mutations.geek.resetGeeks](state) {
      state.allGeeks = [];
    },
    [Mutations.geek.setGeekIdSharedFilter](state, geekSharedFilter: GeekItem) {
      state.geekSharedFilter = geekSharedFilter;
    },
  },

  getters: {
    [Getters.geek.filtered](state) {
      return state.allGeeks.reduce((filtered: LabeledGeek[], geek: GeekItem) => {
        filtered.push({...geek, label: buildGeekFullName(geek).toLowerCase()});
        return filtered;
      }, []);
    },

    [Getters.geek.groupedAdmins](state): AdminGroup[] {
      const groupedByKeyAsProperty = groupBy(state.admins, nameof<any>('jobTitle'));
      return Object.keys(groupedByKeyAsProperty)
        .map((key) => ({ jobTitle: key, admins: groupedByKeyAsProperty[key] }));
    },
  },

  actions: {
    async [Actions.geek.fetchCurrent]({ commit }): Promise<Geek> {
      const geek = await geeksApi.fetchCurrent();
      commit(Mutations.geek.setCurrent, geek);

      return geek;
    },
    async [Actions.geek.fetchCsrfToken]({ commit }): Promise<void> {
      const token = await geeksApi.fetchCsrfToken();
      commit(Mutations.geek.setCsrfToken, token);
    },
    async [Actions.geek.fetchGeeks]({ commit }, searchPhrase: string): Promise<void> {
      if (searchPhrase.length < SELECT_SEARCH_CHAR_THRESHOLD) {
        commit(Mutations.geek.resetGeeks);
        return;
      }
      const filteredGeeks = await geeksApi.fetchGeeks(searchPhrase);
      commit(Mutations.geek.setGeeks, filteredGeeks);
    },
    async [Actions.geek.fetchAdmins]({ commit }): Promise<void> {
      const admins = await geeksApi.fetchAdmins();
      commit(Mutations.geek.setAdmins, admins);
    },
    async [Actions.geek.fetchSuperAdmins]({ commit }): Promise<void> {
      const superAdmins = await geeksApi.fetchSuperAdmins();
      commit(Mutations.geek.setSuperAdmins, superAdmins);
    },
    async [Actions.geek.fetchGeekBasicInfo]({ commit }, geekId?: string): Promise<void> {
      const geekBasicInfo = await geeksApi.fetchGeekBasicInfo(geekId);
      commit(Mutations.geek.setGeekBasicInfo, geekBasicInfo);
    },
    async [Actions.geek.getGeekBasicInfo](_, geekId?: string): Promise<GeekItem> {
      const geekBasicInfo = await geeksApi.fetchGeekBasicInfo(geekId);
      return Promise.resolve(geekBasicInfo);
    },
    async [Actions.geek.setNames](_, geekNames: GeekNames): Promise<void> {
      await geeksApi.setNames(geekNames);
    },
    async [Actions.geek.setNick]({ commit }, nick: string): Promise<void> {
      const geekBasicInfo = await geeksApi.setNick(nick);
      commit(Mutations.geek.setGeekBasicInfo, geekBasicInfo);
    },
    async [Actions.geek.setAvatar](_, { imageType, data }): Promise<void> {
      await geeksApi.setAvatar(imageType, data);
    },
    async [Actions.geek.fetchGeekJobInfo]({ commit }): Promise<void> {
      const geekJobInfo = await geeksApi.fetchGeekJobInfo();
      commit(Mutations.geek.setGeekJobInfo, geekJobInfo);
    },
    [Actions.geek.resetGeekInfo]({ commit }) {
      commit(Mutations.geek.setGeekBasicInfo, Empty.geek);
    },
    [Actions.geek.resetGeeks]({ commit }) {
      commit(Mutations.geek.resetGeeks);
    },
    [Actions.geek.updateGeekIdSharedFilter]({ commit }, geekIdSharedFilter: string) {
      commit(Mutations.geek.setGeekIdSharedFilter, geekIdSharedFilter);
    },
  },
};
